import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Twitter, RefreshCw } from 'lucide-react';
import { getRandomCard, TarotCard } from '../data/tarotDeck';
import TarotCardVisual from '../components/TarotCardVisual';
import StarterCta from '../components/StarterCta';

const DailyCard: React.FC = () => {
    const [cardState, setCardState] = useState<'initial' | 'pulling' | 'revealed'>('initial');
    const [selectedCard, setSelectedCard] = useState<TarotCard | null>(null);
    const [pulledCards, setPulledCards] = useState<number[]>([]);

    useEffect(() => {
        // Load pulled cards from session storage to prevent repeats in same session
        const stored = sessionStorage.getItem('mystic_pulled_cards');
        if (stored) {
            setPulledCards(JSON.parse(stored));
        }
    }, []);

    const handleCardPull = () => {
        if (cardState !== 'initial') return;

        setCardState('pulling');

        // Simulate "shuffling/choosing" delay
        setTimeout(() => {
            const card = getRandomCard(pulledCards);
            setSelectedCard(card);

            // Update session storage
            const newPulled = [...pulledCards, card.id];
            setPulledCards(newPulled);
            sessionStorage.setItem('mystic_pulled_cards', JSON.stringify(newPulled));

            setCardState('revealed');
        }, 1200);
    };

    const handleReset = () => {
        setCardState('initial');
        setSelectedCard(null);
    };

    const shareUrl = "https://mysticprana.in/daily-card";
    const shareText = selectedCard
        ? `I pulled ${selectedCard.name} today. "${selectedCard.uprightMeaning.split('.')[0]}." Pull yours at:`
        : "Get your daily Tarot insight at Mystic Prana.";

    const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` // FB doesn't accept pre-fill text easily anymore
    };

    return (
        <div className="overflow-hidden bg-gradient-to-b from-lavender-100/70 via-cream to-cream">
            <div className="relative z-10 max-w-5xl mx-auto px-6 pt-32 pb-20">
                {/* Header */}
                <div className="text-center mb-12">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="font-fraunces text-4xl sm:text-6xl font-medium text-plum mb-4"
                    >
                        Your Daily Insight
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-plum-soft max-w-xl mx-auto"
                    >
                        Pause, breathe, and let the universe reveal the message you need right now.
                    </motion.p>
                </div>

                {/* Card Stage */}
                <div className="min-h-[500px] flex flex-col items-center justify-center relative perspective-1000">
                    <AnimatePresence mode="wait">

                        {/* INITIAL STATE: Fan of Cards */}
                        {cardState === 'initial' && (
                            <motion.div
                                className="relative h-80 w-full max-w-lg flex items-center justify-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.5 } }}
                            >
                                {/* Fan of 7 cards */}
                                {[...Array(7)].map((_, index) => {
                                    // Calculate rotation and position for fan effect
                                    const rotate = (index - 3) * 5; // -15 to +15 deg
                                    const x = (index - 3) * 15; // spread horizontally
                                    const y = Math.abs(index - 3) * 5; // arc shape (sides lower)

                                    return (
                                        <motion.div
                                            key={index}
                                            data-testid="deck-card"
                                            className="absolute w-48 h-72 rounded-xl border border-lavender-300 bg-gradient-to-br from-[#3A2752] to-plum cursor-pointer shadow-xl origin-bottom focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lavender-600"
                                            style={{
                                                rotate: rotate,
                                                x: x,
                                                y: y,
                                                zIndex: index
                                            }}
                                            whileHover={{
                                                y: -20,
                                                scale: 1.05,
                                                zIndex: 10,
                                                boxShadow: "0 18px 30px -12px rgba(86, 57, 131, 0.45)"
                                            }}
                                            onClick={handleCardPull}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleCardPull();
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Draw card ${index + 1} of 7`}
                                        >
                                            {/* Card Back Pattern */}
                                            <div
                                                className="w-full h-full opacity-25 pointer-events-none mix-blend-screen"
                                                style={{
                                                    backgroundImage: `radial-gradient(1px 1px at 15px 15px, #DCCFF0, rgba(0,0,0,0)), radial-gradient(1px 1px at 35px 35px, #ffffff, rgba(0,0,0,0))`,
                                                    backgroundSize: '40px 40px'
                                                }}
                                            />
                                            <div className="absolute inset-2.5 rounded-lg border border-lavender-200/40 pointer-events-none flex items-center justify-center">
                                                <div className="w-20 h-20 rounded-full border border-lavender-200/50 flex items-center justify-center">
                                                    <div className="w-10 h-10 rotate-45 border border-lavender-200/60 flex items-center justify-center bg-lavender-200/10">
                                                        <span className="text-lavender-200 text-xs -rotate-45">✦</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                                <motion.div
                                    className="absolute -bottom-16 text-plum-soft tracking-[0.25em] text-xs"
                                    animate={{ opacity: [0.5, 1, 0.5] }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                >
                                    CHOOSE A CARD THAT CALLS TO YOU
                                </motion.div>
                            </motion.div>
                        )}

                        {/* PULLING STATE: Transition */}
                        {cardState === 'pulling' && (
                            <motion.div
                                className="flex items-center justify-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <div className="relative w-56 h-80 preserve-3d animate-pulse">
                                    <div className="absolute inset-0 bg-lavender-300/50 blur-3xl rounded-full"></div>
                                    <div className="relative w-full h-full bg-gradient-to-br from-[#3A2752] to-plum rounded-xl border border-lavender-300 shadow-xl"></div>
                                </div>
                            </motion.div>
                        )}

                        {/* REVEALED STATE: Result */}
                        {cardState === 'revealed' && selectedCard && (
                            <motion.div
                                className="w-full max-w-4xl mx-auto"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                            >
                                <div className="flex flex-col md:flex-row gap-12 items-center md:items-start">
                                    {/* Card Visual */}
                                    <div className="flex-shrink-0 relative group">
                                        <motion.div
                                            className="w-64 h-96 rounded-2xl overflow-hidden shadow-2xl relative z-10"
                                            initial={{ rotateY: 180 }}
                                            animate={{ rotateY: 0 }}
                                            transition={{ duration: 0.8 }}
                                        >
                                            <TarotCardVisual card={selectedCard} />
                                        </motion.div>
                                        <div className="absolute inset-0 bg-lavender-300/40 blur-3xl -z-10 group-hover:bg-lavender-300/60 transition-all duration-700"></div>
                                    </div>

                                    {/* Card Meaning */}
                                    <div className="flex-1 space-y-8">
                                        <div>
                                            <h2 className="font-fraunces text-4xl font-medium text-plum mb-2">{selectedCard.name}</h2>
                                            <div className="flex flex-wrap gap-2 mb-6">
                                                {selectedCard.keywords.map((kw, i) => (
                                                    <span key={i} className="px-3 py-1 rounded-full text-xs uppercase tracking-wider text-lavender-700 bg-lavender-100">
                                                        {kw}
                                                    </span>
                                                ))}
                                            </div>
                                            <p className="font-fraunces text-lg text-plum-soft leading-relaxed border-l-2 border-lavender-400 pl-6 italic">
                                                "{selectedCard.uprightMeaning}"
                                            </p>
                                        </div>

                                        <div className="border-l-2 border-lavender-200 pl-6">
                                            <h3 className="font-fraunces text-xl text-lavender-700 mb-2">Reflection</h3>
                                            <p className="text-plum">{selectedCard.reflectionPrompt}</p>
                                        </div>

                                        <div className="flex flex-wrap gap-4 pt-4">
                                            <button
                                                onClick={handleReset}
                                                className="flex items-center gap-2 px-6 py-3 rounded-full border border-lavender-300 text-lavender-700 hover:bg-lavender-50 transition-colors text-sm font-bold"
                                            >
                                                <RefreshCw size={16} aria-hidden="true" /> Pull another card
                                            </button>

                                            <div className="flex items-center gap-2 ml-auto">
                                                <span className="text-xs text-plum-soft uppercase tracking-wider mr-2">Share:</span>
                                                <a href={shareLinks.whatsapp} aria-label="Share on WhatsApp" target="_blank" rel="noopener noreferrer" className="p-2 bg-lavender-100 text-lavender-700 rounded-full hover:bg-[#25D366] hover:text-white transition-colors"><Share2 size={18} aria-hidden="true" /></a>
                                                <a href={shareLinks.twitter} aria-label="Share on X" target="_blank" rel="noopener noreferrer" className="p-2 bg-lavender-100 text-lavender-700 rounded-full hover:bg-[#1DA1F2] hover:text-white transition-colors"><Twitter size={18} aria-hidden="true" /></a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                                {/* Invitation to go deeper */}
                                <motion.div
                                    className="mt-16 pt-10 border-t border-lavender-200 text-center"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                >
                                    <h3 className="font-fraunces text-2xl mb-2">Want to go deeper?</h3>
                                    <p className="text-plum-soft mb-6 max-w-md mx-auto">Share what is on your mind, and Madhulika will take it from there.</p>
                                    <StarterCta source="daily-card" />
                                </motion.div>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default DailyCard;
